import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { NotebookPen, Reply } from "lucide-react";
import { toast } from "sonner";
import { Btn } from "@/components/Btn";
import { errText } from "@/lib/api";
import { api } from "@/lib/api";
import { useSentReports } from "@/components/SentReports";

function ReplyBox({ n, i }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/reports/notes/${n.id}/reply`, { text: text.trim() });
      toast.success("Reply shared on your report page");
      setOpen(false); setText("");
      qc.invalidateQueries({ queryKey: ["sent-reports"] });
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  if (open) {
    return (
      <div className="mt-2" data-testid={`psych-reply-box-${i}`}>
        <textarea className="tf-input !min-h-[64px] text-sm" value={text} onChange={(e) => setText(e.target.value)} maxLength={500} autoFocus
          placeholder="e.g. Thank you — see you Thursday." data-testid={`psych-reply-input-${i}`} />
        <div className="mt-2 flex items-center gap-2">
          <Btn size="md" label="Send reply" loading={busy} disabled={!text.trim()} onClick={send} testId={`psych-reply-send-${i}`} />
          <Btn size="md" variant="ghost" label="Cancel" onClick={() => setOpen(false)} testId={`psych-reply-cancel-${i}`} />
          <span className="ml-auto text-xs text-t-muted">{text.length}/500</span>
        </div>
      </div>
    );
  }
  return n.reply ? (
    <div className="mt-2 ml-4 rounded-xl bg-t-brand3 px-3 py-2" data-testid={`psych-reply-${i}`}>
      <p className="text-xs text-t-muted">You replied · {new Date(n.reply.at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}</p>
      <p className="text-sm text-t-on whitespace-pre-line">{n.reply.text}</p>
    </div>
  ) : (
    <button onClick={() => setOpen(true)} className="mt-2 inline-flex items-center gap-1.5 text-[13px] font-semibold text-t-brand hover:opacity-80 transition-opacity" data-testid={`psych-reply-button-${i}`}><Reply size={14} /> Reply</button>
  );
}

export function PsychNotesCard() {
  const qc = useQueryClient();
  const { data: reports = [] } = useSentReports(true);
  const [fresh, setFresh] = useState(null);
  const marked = useRef(false);
  const notes = reports.flatMap((r) => (r.notes || []).map((n) => ({ ...n, report: r }))).sort((a, b) => b.at.localeCompare(a.at));
  useEffect(() => {
    if (fresh !== null || !reports.length) return;
    const unread = new Set(notes.filter((n) => !n.read).map((n) => n.id));
    setFresh(unread);
    if (unread.size && !marked.current) {
      marked.current = true;
      api.post("/reports/notes/read").then(() => qc.invalidateQueries({ queryKey: ["sent-reports"] })).catch(() => {});
    }
  }, [reports, fresh, notes, qc]);
  if (!notes.length) return null;
  return (
    <div className="tf-card p-5 mt-6" data-testid="psych-notes-card">
      <p className="flex items-center gap-2 font-bold text-t-on"><NotebookPen size={18} className="text-t-brand" /> Notes from your psychologist</p>
      <ul className="mt-3 flex flex-col gap-2 max-h-72 overflow-y-auto pr-1">
        {notes.map((n, i) => (
          <li key={n.id} className="rounded-2xl bg-t-surface2 border border-t-border px-4 py-3" data-testid={`psych-note-${i}`}>
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-t-muted">{n.report.therapistName || n.report.to} · on your {n.report.title} · {new Date(n.at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}</p>
              {fresh?.has(n.id) ? <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-t-brand text-t-onbrand" data-testid={`psych-note-new-${i}`}>New</span> : null}
            </div>
            <p className="mt-1 text-t-on whitespace-pre-line">{n.text}</p>
            <ReplyBox n={n} i={i} />
          </li>
        ))}
      </ul>
    </div>
  );
}
